import React, { useEffect, useState } from 'react';
import { Button, Form, Grid, Header, Message, Modal, Segment, Table, Label, Popup, Pagination } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';

const InviteCode = () => {
  const [inviteCodes, setInviteCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedInviteCode, setSelectedInviteCode] = useState(null);
  const [inviteCodeUsers, setInviteCodeUsers] = useState([]);
  const [userPagination, setUserPagination] = useState({
    activePage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  });
  // 生成8位大写字母和数字的邀请码
  function generateRandomCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const [newCode, setNewCode] = useState({
    code: generateRandomCode(),
    quota: 0,
    action_quota: 0,
    max_uses: 0,
    used_count: 0,
    description: ''
  });

  const loadInviteCodes = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/invite-codes');
      const { success, message, data } = res.data;
      if (success) {
        setInviteCodes(data);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('加载邀请码失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadInviteCodes();
  }, []);

  const handleAddCode = async () => {
    if (!newCode.code.trim()) {
      showError('邀请码生成失败，请刷新页面重试');
      return;
    }
    if (newCode.quota < 0) {
      showError('额度不能为负数');
      return;
    }
    if (newCode.max_uses < 0) {
      showError('最大使用次数不能为负数');
      return;
    }

    setLoading(true);
    try {
      // 确保数字字段是数字类型而不是字符串
      const requestData = {
        code: newCode.code,
        quota: parseInt(newCode.quota) || 0,
        action_quota: parseInt(newCode.action_quota) || 0,
        max_uses: parseInt(newCode.max_uses) || 0,
        used_count: parseInt(newCode.used_count) || 0,
        note: newCode.description // 将description映射为note
      };
      
      const res = await API.post('/api/admin/invite-codes', requestData);
      const { success, message } = res.data;
      if (success) {
        showSuccess('邀请码创建成功');
        setShowAddModal(false);
        setNewCode({
          code: generateRandomCode(),
          quota: 0,
          action_quota: 0,
          max_uses: 0,
          used_count: 0,
          description: ''
        });
        await loadInviteCodes();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('创建邀请码失败');
    }
    setLoading(false);
  };

  // 删除邀请码二次确认弹窗相关状态
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inviteCodeToDelete, setInviteCodeToDelete] = useState(null);

  const handleDeleteCode = async (id) => {
    setLoading(true);
    try {
      const res = await API.delete(`/api/admin/invite-codes/${id}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('邀请码删除成功');
        await loadInviteCodes();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('删除邀请码失败');
    }
    setLoading(false);
    setShowDeleteModal(false);
    setInviteCodeToDelete(null);
  };

  const loadInviteCodeUsers = async (inviteCodeId, page = 1) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/admin/invite-codes/${inviteCodeId}/users?page=${page}&size=${userPagination.pageSize}`);
      const { success, message, data, total, page: currentPage, size } = res.data;
      if (success) {
        setInviteCodeUsers(data || []);
        const totalPages = Math.ceil(total / size);
        setUserPagination(prev => ({
          ...prev,
          activePage: currentPage,
          totalPages: totalPages,
          totalItems: total,
          pageSize: size
        }));
      } else {
        showError(message);
      }
    } catch (error) {
      showError('加载用户列表失败');
    }
    setLoading(false);
  };

  const handleViewUsers = async (inviteCode) => {
    setSelectedInviteCode(inviteCode);
    setShowUsersModal(true);
    setUserPagination(prev => ({ ...prev, activePage: 1 }));
    await loadInviteCodeUsers(inviteCode.id, 1);
  };

  const handleUserPageChange = (e, { activePage }) => {
    if (selectedInviteCode) {
      loadInviteCodeUsers(selectedInviteCode.id, activePage);
    }
  };

  const handlePauseCode = async (id) => {
    setLoading(true);
    try {
      const res = await API.post(`/api/admin/invite-codes/${id}/pause`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('邀请码已暂停');
        await loadInviteCodes();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('暂停邀请码失败');
    }
    setLoading(false);
  };

  const handleResumeCode = async (id) => {
    setLoading(true);
    try {
      const res = await API.post(`/api/admin/invite-codes/${id}/resume`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('邀请码已继续');
        await loadInviteCodes();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('继续邀请码失败');
    }
    setLoading(false);
  };

  const handleInputChange = (e, { name, value }) => {
    // 只允许编辑 quota/max_uses/used_count/description
    if (name === 'quota' || name === 'action_quota' || name === 'max_uses' || name === 'used_count' || name === 'description') {
      let processedValue = value;
      if (name === 'quota' || name === 'action_quota' || name === 'max_uses' || name === 'used_count') {
        processedValue = value === '' ? 0 : parseInt(value) || 0;
      }
      setNewCode({
        ...newCode,
        [name]: processedValue
      });
    }
  };



  return (
    <Grid columns={1}>
      <Grid.Column>
        <Segment>
          <Header as='h3'>邀请码管理</Header>
          <Message info>
            邀请码用于控制新用户注册，启用邀请码后，新用户注册时必须提供有效的邀请码。
          </Message>
          
          <Button
            color='green'
            onClick={() => setShowAddModal(true)}
            style={{ marginBottom: '20px' }}
          >
            创建邀请码
          </Button>

          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>邀请码</Table.HeaderCell>
                <Table.HeaderCell>Token 额度</Table.HeaderCell>
                <Table.HeaderCell>Action 配额</Table.HeaderCell>
                <Table.HeaderCell>最大使用次数</Table.HeaderCell>
                <Table.HeaderCell>已使用次数</Table.HeaderCell>
                <Table.HeaderCell>状态</Table.HeaderCell>
                <Table.HeaderCell>备注</Table.HeaderCell>
                <Table.HeaderCell>创建时间</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {inviteCodes.map((code) => (
                <Table.Row key={code.id}>
                  <Table.Cell>
                    <code>{code.code}</code>
                  </Table.Cell>
                  <Table.Cell>{code.quota}</Table.Cell>
                  <Table.Cell>{code.action_quota || 0}</Table.Cell>
                  <Table.Cell>
                    {code.max_uses === 0 ? '无限制' : code.max_uses}
                  </Table.Cell>
                  <Table.Cell>{code.used_count}</Table.Cell>
                  <Table.Cell>
                    {code.status === 1 ? (
                      <span style={{ color: 'green' }}>启用</span>
                    ) : code.status === 2 ? (
                      <span style={{ color: 'red' }}>禁用</span>
                    ) : code.status === 3 ? (
                      <span style={{ color: 'orange' }}>暂停</span>
                    ) : (
                      <span>未知</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>{code.description || '-'}</Table.Cell>
                  <Table.Cell>
                    {new Date(code.created_at).toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>
                    <Popup
                      content={`查看使用此邀请码的用户 (${code.used_count}人)`}
                      trigger={
                        <Button
                          color='blue'
                          size='small'
                          onClick={() => handleViewUsers(code)}
                        >
                          用户列表
                        </Button>
                      }
                    />
                    {code.status === 1 ? (
                      <Button
                        color='orange'
                        size='small'
                        onClick={() => handlePauseCode(code.id)}
                        style={{ marginLeft: '5px' }}
                      >
                        暂停
                      </Button>
                    ) : code.status === 3 ? (
                      <Button
                        color='green'
                        size='small'
                        onClick={() => handleResumeCode(code.id)}
                        style={{ marginLeft: '5px' }}
                      >
                        继续
                      </Button>
                    ) : null}
                    <Button
                      color='red'
                      size='small'
                      onClick={() => {
                        setInviteCodeToDelete(code);
                        setShowDeleteModal(true);
                      }}
                      style={{ marginLeft: '5px' }}
                    >
                      删除
                    </Button>
                  {/* 删除邀请码二次确认 Modal */}
                  <Modal
                    open={showDeleteModal}
                    size='small'
                    onClose={() => {
                      setShowDeleteModal(false);
                      setInviteCodeToDelete(null);
                    }}
                  >
                    <Modal.Header>删除邀请码</Modal.Header>
                    <Modal.Content>
                      <p>确定要删除邀请码 <b>{inviteCodeToDelete?.code}</b> 吗？此操作不可恢复。</p>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button onClick={() => {
                        setShowDeleteModal(false);
                        setInviteCodeToDelete(null);
                      }}>
                        取消
                      </Button>
                      <Button negative onClick={async () => {
                        if (inviteCodeToDelete) {
                          await handleDeleteCode(inviteCodeToDelete.id);
                        }
                      }}>
                        确认删除
                      </Button>
                    </Modal.Actions>
                  </Modal>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>

        <Modal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          size='small'
        >
          <Modal.Header>创建邀请码</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Group widths={2}>
                <Form.Input
                  label='邀请码'
                  name='code'
                  value={newCode.code}
                  readOnly
                  placeholder='自动生成的邀请码'
                  required
                />
              </Form.Group>
              <Form.Group widths={3}>
                <Form.Input
                  label='Token 额度'
                  name='quota'
                  type='number'
                  value={newCode.quota}
                  onChange={handleInputChange}
                  placeholder='0'
                  min='0'
                />
                <Form.Input
                  label='Action 配额'
                  name='action_quota'
                  type='number'
                  value={newCode.action_quota}
                  onChange={handleInputChange}
                  placeholder='0，0表示使用系统默认'
                  min='0'
                />
                <Form.Input
                  label='最大使用次数'
                  name='max_uses'
                  type='number'
                  value={newCode.max_uses}
                  onChange={handleInputChange}
                  placeholder='0表示无限制'
                  min='0'
                />
              </Form.Group>
              <Form.Input
                label='备注'
                name='description'
                value={newCode.description}
                onChange={handleInputChange}
                placeholder='可选'
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setShowAddModal(false)}>
              取消
            </Button>
            <Button
              color='green'
              onClick={handleAddCode}
              loading={loading}
            >
              创建
            </Button>
          </Modal.Actions>
        </Modal>

        {/* 用户列表模态框 */}
        <Modal
          open={showUsersModal}
          onClose={() => setShowUsersModal(false)}
          size='large'
        >
          <Modal.Header>
            邀请码用户列表 - {selectedInviteCode?.code}
            <Label color='blue' style={{ marginLeft: '10px' }}>
              共 {userPagination.totalItems} 人
            </Label>
          </Modal.Header>
          <Modal.Content>
            {inviteCodeUsers.length === 0 ? (
              <Message info>暂无用户使用此邀请码</Message>
            ) : (
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>用户名</Table.HeaderCell>
                    <Table.HeaderCell>显示名称</Table.HeaderCell>
                    <Table.HeaderCell>邮箱</Table.HeaderCell>
                    <Table.HeaderCell>角色</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>注册时间</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {inviteCodeUsers.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>{user.username}</Table.Cell>
                      <Table.Cell>{user.display_name || '-'}</Table.Cell>
                      <Table.Cell>{user.email || '-'}</Table.Cell>
                      <Table.Cell>
                        {user.role === 1 ? '普通用户' : 
                         user.role === 10 ? '管理员' : 
                         user.role === 100 ? '超级管理员' : '未知'}
                      </Table.Cell>
                      <Table.Cell>
                        {user.status === 1 ? (
                          <Label color='green'>已激活</Label>
                        ) : user.status === 2 ? (
                          <Label color='red'>已封禁</Label>
                        ) : (
                          <Label>未知</Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {new Date(user.created_at).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          as={Link}
                          to={`/user/${user.id}`}
                          color='blue'
                          size='small'
                        >
                          管理用户
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
            {inviteCodeUsers.length > 0 && userPagination.totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Pagination
                  activePage={userPagination.activePage}
                  totalPages={userPagination.totalPages}
                  onPageChange={handleUserPageChange}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{ content: '上一页', icon: 'left arrow' }}
                  nextItem={{ content: '下一页', icon: 'right arrow' }}
                />
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setShowUsersModal(false)}>
              关闭
            </Button>
          </Modal.Actions>
        </Modal>
      </Grid.Column>
    </Grid>
  );
};

export default InviteCode;
